import React, { useState, useEffect } from 'react';
import { Upload, File, Trash2, Download, Eye, Lock, FileText, Image, FileSpreadsheet, Calendar, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Ko';
  const k = 1024;
  const sizes = ['octets', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

export default function DocumentUpload({ entityType, entityId, title = "Documents" }) {
  const { currentUser, canViewDocuments } = useApp();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  const canView = canViewDocuments(entityType);
  const canDelete = ['Admin', 'RH', 'HSE'].includes(currentUser?.role);

  useEffect(() => {
    loadDocuments();
  }, [entityType, entityId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await api.getDocuments(entityType, entityId);
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error(`Type de fichier non autorisé. Types autorisés: ${allowedTypes.join(', ')}`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 10MB)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setUploading(true);
      await api.uploadDocument(
        selectedFile,
        entityType,
        entityId,
        description || '',
        currentUser.id,
        `${currentUser.prenom} ${currentUser.nom}`
      );
      toast.success('Document téléchargé avec succès');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setDescription('');
      loadDocuments();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.message || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    if (!canView) {
      toast.error('Accès non autorisé. Seuls les RH, HSE, Manager et Directeurs peuvent consulter les documents.');
      return;
    }
    try {
      await api.downloadDocument(doc.id, currentUser?.role || '');
    } catch (error) {
      toast.error(error.message || 'Erreur lors du téléchargement');
    }
  };

  const handlePreview = async (doc) => {
    if (!canView) {
      toast.error('Vous n\'avez pas les droits pour visualiser ce document.');
      return;
    }
    try {
      await api.viewDocument(doc.id, currentUser?.role || '');
      toast.success('Document ouvert dans un nouvel onglet');
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error(error.message || 'Erreur lors de la visualisation du document');
    }
  };

  const handleDelete = async () => {
    if (!selectedDoc) return;

    try {
      await api.deleteDocument(selectedDoc.id, currentUser?.role || '');
      toast.success('Document supprimé');
      setDeleteDialogOpen(false);
      setSelectedDoc(null);
      loadDocuments();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return <File className="h-5 w-5 text-gray-500" />;
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return <FileText className="h-5 w-5 text-red-500" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="h-5 w-5 text-green-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="h-5 w-5 text-blue-500" />;
    if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="h-5 w-5 text-emerald-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const getFileTypeBadge = (filename) => {
    if (!filename) return null;
    const ext = filename.split('.').pop()?.toUpperCase() || '';
    const colors = {
      'PDF': 'bg-red-100 text-red-700',
      'JPG': 'bg-green-100 text-green-700',
      'JPEG': 'bg-green-100 text-green-700',
      'PNG': 'bg-green-100 text-green-700',
      'DOC': 'bg-blue-100 text-blue-700',
      'DOCX': 'bg-blue-100 text-blue-700'
    };
    return (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${colors[ext] || 'bg-gray-100 text-gray-700'}`}>
        {ext}
      </span>
    );
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3 bg-gradient-to-r from-slate-50 to-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2 text-gray-800">
            <File className="h-5 w-5 text-blue-600" />
            {title}
            {documents.length > 0 && (
              <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                {documents.length}
              </Badge>
            )}
            {!canView && (
              <span className="text-xs text-amber-600 flex items-center gap-1 ml-2">
                <Lock className="h-3 w-3" /> Accès restreint
              </span>
            )}
          </CardTitle>
          <Button size="sm" onClick={() => setUploadDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Upload className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <File className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 font-medium">Aucun document attaché</p>
            <p className="text-xs text-gray-400 mt-1">Cliquez sur "Ajouter" pour joindre un fichier</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200 group"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-blue-100 transition-colors">
                    {getFileIcon(doc.filename)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc.filename || 'Document sans nom'}</p>
                      {getFileTypeBadge(doc.filename)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {formatFileSize(doc.file_size)}
                      </span>
                      {doc.uploaded_by_nom && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {doc.uploaded_by_nom}
                        </span>
                      )}
                      {doc.date_upload && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(doc.date_upload)}
                        </span>
                      )}
                    </div>
                    {doc.description && doc.description !== 'undefined' && doc.description.trim() !== '' && (
                      <p className="text-xs text-gray-600 mt-1.5 italic bg-gray-50 px-2 py-1 rounded">
                        "{doc.description}"
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                  {canView ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(doc)}
                        title="Visualiser"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        title="Télécharger"
                        className="text-gray-600 hover:text-gray-700 hover:bg-gray-100"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      title="Accès restreint aux RH, HSE, Manager et Directeurs"
                      className="text-gray-400"
                    >
                      <Lock className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedDoc(doc);
                        setDeleteDialogOpen(true);
                      }}
                      title="Supprimer"
                      className="text-red-500 hover:text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Ajouter un document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-medium">Fichier <span className="text-red-500">*</span></Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <Input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="cursor-pointer"
                />
              </div>
              <p className="text-xs text-gray-500">
                Formats acceptés: PDF, JPG, PNG, DOC, DOCX (max 10 Mo)
              </p>
            </div>
            {selectedFile && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-3">
                  {getFileIcon(selectedFile.name)}
                  <div>
                    <p className="text-sm font-medium text-blue-900">{selectedFile.name}</p>
                    <p className="text-xs text-blue-700">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-medium">Description (optionnel)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Certificat médical, Attestation CACES..."
                className="text-sm"
              />
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <Lock className="h-4 w-4 flex-shrink-0" />
                <span>Ce document sera accessible uniquement aux RH, HSE, Manager et Directeurs</span>
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!selectedFile || uploading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {uploading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Télécharger
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirmer la suppression
            </AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <span className="font-medium">{selectedDoc?.filename}</span> ? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
